// src/web/routes/f/$token.tsx - Public folder share page

import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { formatFileSize } from '@shared/formatters'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
	AlertCircleIcon,
	ArrowLeftIcon,
	ChevronRightIcon,
	DownloadIcon,
	FileImage,
	Folder,
	FolderOpen,
	LoaderIcon,
	LockIcon,
	ShieldCheckIcon,
} from 'lucide-react'
import { useCallback, useState } from 'react'
import { z } from 'zod'
import { QueryBoundary } from '@/components/ErrorBoundary'
import { AssetPreview } from '@/components/features/AssetPreview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileTypeIcon } from '@/components/ui/FileTypeIcon'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import {
	useFolderShareInfo,
	usePublicFolderAssetDetail,
	usePublicFolderAssets,
	usePublicFolderBreadcrumbs,
	usePublicFolderChildren,
	useVerifyFolderSharePassword,
} from '@/lib/hooks/useFolderShare'
import { cn } from '@/lib/utils'

// Validated search params (mirrors home.tsx). z.coerce + .catch() means a bad
// page (0, "abc", arrays) falls back instead of the cast lies the old code made.
const folderShareSearchSchema = z.object({
	folderId: z.string().optional(),
	assetId: z.string().optional(),
	page: z.coerce.number().int().min(1).catch(1).default(1),
	sortBy: z.string().default('date_staged'),
	sortOrder: z.enum(['asc', 'desc']).catch('desc').default('desc'),
})

export const Route = createFileRoute('/f/$token')({
	component: PublicFolderPage,
	validateSearch: folderShareSearchSchema,
})

// ─── localStorage unlock persistence ───

const UNLOCK_STORAGE_PREFIX = 'dam_folder_share_unlock_'

interface StoredUnlock {
	previewToken: string
}

function getStoredUnlock(token: string): StoredUnlock | null {
	try {
		const stored = localStorage.getItem(`${UNLOCK_STORAGE_PREFIX}${token}`)
		if (!stored) return null
		return JSON.parse(stored) as StoredUnlock
	} catch {
		return null
	}
}

function saveUnlock(token: string, previewToken: string): void {
	try {
		localStorage.setItem(`${UNLOCK_STORAGE_PREFIX}${token}`, JSON.stringify({ previewToken }))
	} catch {}
}

// ─── Folder preview grid ───

function FolderPreviewGrid({
	previews,
	subfolderCount,
	assetCount,
	folderId,
}: {
	previews: Array<{ url: string; isVideo: boolean }>
	subfolderCount: number
	assetCount: number
	folderId: string
}) {
	const [errorSet, setErrorSet] = useState<Set<number>>(new Set())

	if (previews.length === 0) {
		if (subfolderCount === 0 && assetCount === 0) {
			return (
				<div className="h-full w-full flex items-center justify-center bg-muted">
					<FolderOpen className="w-8 h-8 text-muted-foreground/50" />
				</div>
			)
		}
		if (subfolderCount > 0) {
			const count = Math.min(subfolderCount, 4)
			return (
				<div
					className={cn(
						'h-full w-full gap-px',
						count === 1
							? 'flex items-center justify-center bg-muted'
							: 'grid grid-cols-2 grid-rows-2'
					)}
				>
					{count === 1 ? (
						<Folder className="w-8 h-8 text-muted-foreground/50" />
					) : (
						Array.from({ length: count }).map((_, i) => (
							<div
								// biome-ignore lint/suspicious/noArrayIndexKey: Static icon placeholders
								key={`${folderId}-fi-${i}`}
								className="flex items-center justify-center bg-muted"
							>
								<Folder className="w-5 h-5 text-muted-foreground/50" />
							</div>
						))
					)}
				</div>
			)
		}
		return (
			<div className="h-full w-full flex items-center justify-center bg-muted">
				<FileImage className="w-8 h-8 text-muted-foreground/50" />
			</div>
		)
	}

	const items = previews.slice(0, 4)
	if (items.length === 1) {
		return (
			<div className="h-full w-full">
				{errorSet.has(0) ? (
					<div className="w-full h-full bg-muted flex items-center justify-center">
						<FileImage className="w-4 h-4 text-muted-foreground/50" />
					</div>
				) : (
					<img
						src={items[0].url}
						alt=""
						className="w-full h-full object-cover"
						loading="lazy"
						onError={() => setErrorSet((s) => new Set(s).add(0))}
					/>
				)}
			</div>
		)
	}

	return (
		<div
			className={cn(
				'h-full w-full gap-px',
				items.length <= 2 ? 'grid grid-cols-2' : 'grid grid-cols-2 grid-rows-2'
			)}
		>
			{items.map((media, i) => (
				<div
					// biome-ignore lint/suspicious/noArrayIndexKey: Preview grid with stable parent key
					key={`${folderId}-p-${i}`}
					className={cn(
						'relative overflow-hidden bg-muted',
						items.length === 3 && i === 0 && 'row-span-2'
					)}
				>
					{errorSet.has(i) ? (
						<div className="absolute inset-0 flex items-center justify-center">
							<FileImage className="w-3 h-3 text-muted-foreground/50" />
						</div>
					) : (
						<img
							src={media.url}
							alt=""
							className="absolute inset-0 w-full h-full object-cover"
							loading="lazy"
							onError={() => setErrorSet((s) => new Set(s).add(i))}
						/>
					)}
				</div>
			))}
		</div>
	)
}

// ─── Public Folder Card ───

interface PublicFolderCardProps {
	folder: {
		id: string
		name: string
		assetCount: number
		subfolderCount: number
		totalSize: number
		previews: Array<{ url: string; isVideo: boolean }>
	}
	onClick: () => void
	variant?: 'large' | 'compact'
}

function PublicFolderCard({ folder, onClick, variant = 'large' }: PublicFolderCardProps) {
	if (variant === 'compact') {
		return (
			<button
				type="button"
				onClick={onClick}
				className="group flex items-center gap-3 p-3 rounded-lg bg-background border border-border hover:border-primary/50 hover:shadow-sm hover:scale-[1.02] transition-all text-left w-full"
			>
				<div className="w-12 h-12 rounded-lg overflow-hidden bg-muted flex-shrink-0">
					<FolderPreviewGrid
						previews={folder.previews}
						subfolderCount={folder.subfolderCount}
						assetCount={folder.assetCount}
						folderId={folder.id}
					/>
				</div>
				<div className="flex-1 min-w-0">
					<h4 className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
						{folder.name}
					</h4>
					<p className="text-xs text-muted-foreground">
						{folder.subfolderCount > 0 && (
							<>
								{folder.subfolderCount} {folder.subfolderCount === 1 ? 'sub-folder' : 'sub-folders'}
								{folder.assetCount > 0 && <span className="mx-1">&middot;</span>}
							</>
						)}
						{folder.assetCount > 0 && (
							<>
								{folder.assetCount.toLocaleString()} {folder.assetCount === 1 ? 'asset' : 'assets'}
							</>
						)}
						{folder.totalSize > 0 && (
							<>
								<span className="mx-1">&middot;</span>
								{formatFileSize(folder.totalSize)}
							</>
						)}
						{folder.subfolderCount === 0 && folder.assetCount === 0 && 'Empty'}
					</p>
				</div>
				<ChevronRightIcon className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors flex-shrink-0" />
			</button>
		)
	}

	return (
		<button
			type="button"
			onClick={onClick}
			className="group text-left w-full cursor-pointer hover:scale-[1.02] transition-transform duration-200"
		>
			<div className="aspect-square rounded-lg overflow-hidden bg-muted border border-border group-hover:border-white/15 dark:group-hover:border-white/[0.06] group-hover:shadow-md transition-all">
				<FolderPreviewGrid
					previews={folder.previews}
					subfolderCount={folder.subfolderCount}
					assetCount={folder.assetCount}
					folderId={folder.id}
				/>
			</div>
			<h3 className="mt-3 text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">
				{folder.name}
			</h3>
			<p className="text-xs text-muted-foreground">
				{folder.subfolderCount > 0 && (
					<>
						{folder.subfolderCount} {folder.subfolderCount === 1 ? 'sub-folder' : 'sub-folders'}
						<span className="mx-1">&middot;</span>
					</>
				)}
				{folder.assetCount.toLocaleString()} {folder.assetCount === 1 ? 'asset' : 'assets'}
				{folder.totalSize > 0 && (
					<>
						<span className="mx-1">&middot;</span>
						{formatFileSize(folder.totalSize)}
					</>
				)}
			</p>
		</button>
	)
}

// ─── Public Asset Card ───

type PublicAsset = {
	id: string
	thumbnailUrl: string | null
	file: {
		id: string
		filename: string
		size: number
		mediaTypeType: string | null
		mediaTypeSubtype: string | null
		width: number | null
		height: number | null
		duration: number | null
	} | null
}

function PublicAssetCard({ asset, onClick }: { asset: PublicAsset; onClick: () => void }) {
	const [imgError, setImgError] = useState(false)
	const file = asset.file

	return (
		<button
			type="button"
			className="group relative rounded-lg overflow-hidden border border-border hover:border-primary/30 transition-all bg-background cursor-pointer text-left w-full"
			onClick={onClick}
		>
			<div className="aspect-square bg-muted relative">
				{asset.thumbnailUrl && !imgError ? (
					<img
						src={asset.thumbnailUrl}
						alt={file?.filename || ''}
						className="w-full h-full object-cover"
						loading="lazy"
						onError={() => setImgError(true)}
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center">
						{file ? (
							<FileTypeIcon
								type={file.mediaTypeType || 'other'}
								mimeSubtype={file.mediaTypeSubtype || undefined}
								className="w-8 h-8 text-muted-foreground/50"
							/>
						) : (
							<FileImage className="w-8 h-8 text-muted-foreground/50" />
						)}
					</div>
				)}
				{file?.duration && (
					<div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded">
						{Math.floor(file.duration / 60)}:{String(file.duration % 60).padStart(2, '0')}
					</div>
				)}
			</div>
			<div className="p-2">
				<p className="text-xs font-medium text-foreground truncate">
					{file?.filename || 'Unknown'}
				</p>
				<p className="text-[10px] text-muted-foreground">
					{file?.size ? formatFileSize(file.size) : ''}
					{file?.width && file?.height ? ` \u00B7 ${file.width}\u00D7${file.height}` : ''}
				</p>
			</div>
		</button>
	)
}

// ─── Breadcrumbs ───

function PublicBreadcrumbs({
	token,
	folderId,
	previewToken,
	onNavigate,
}: {
	token: string
	folderId?: string
	previewToken?: string | null
	onNavigate: (folderId?: string) => void
}) {
	const { data } = usePublicFolderBreadcrumbs(token, folderId, previewToken, {
		enabled: !!folderId,
	})

	if (!data?.breadcrumbs || data.breadcrumbs.length <= 1) return null

	return (
		<nav className="flex items-center gap-1 text-sm overflow-x-auto min-w-0 pb-1">
			{data.breadcrumbs.map((crumb, i) => (
				<span key={crumb.id} className="flex items-center gap-1 shrink-0">
					{i > 0 && <ChevronRightIcon className="w-3 h-3 text-muted-foreground shrink-0" />}
					{i < data.breadcrumbs.length - 1 ? (
						<button
							type="button"
							onClick={() => (i === 0 ? onNavigate(undefined) : onNavigate(crumb.id))}
							className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[120px]"
						>
							{crumb.name}
						</button>
					) : (
						<span className="text-foreground font-medium truncate max-w-[200px]">{crumb.name}</span>
					)}
				</span>
			))}
		</nav>
	)
}

// ─── Sort Toolbar ───

const SORT_OPTIONS = [
	{ value: 'date_staged', label: 'Date Staged' },
	{ value: 'date_live', label: 'Date Live' },
	{ value: 'filename', label: 'Filename' },
	{ value: 'created_at', label: 'Date Added' },
]

function PublicSortToolbar({
	totalCount,
	sortBy,
	sortOrder,
	onSortByChange,
	onSortOrderToggle,
}: {
	totalCount: number
	sortBy: string
	sortOrder: string
	onSortByChange: (value: string) => void
	onSortOrderToggle: () => void
}) {
	return (
		<div className="flex items-center justify-between gap-4 mb-4">
			<div className="text-sm text-muted-foreground">
				{totalCount > 0 ? (
					<>
						<span className="font-medium text-foreground">{totalCount.toLocaleString()}</span>
						{' assets'}
					</>
				) : (
					<span>No assets</span>
				)}
			</div>
			<div className="flex items-center gap-2">
				<div className="relative">
					<select
						value={sortBy}
						onChange={(e) => onSortByChange(e.target.value)}
						className={cn(
							'appearance-none h-9 pl-3 pr-8 rounded-lg border border-border bg-background/70 backdrop-blur-sm',
							'text-sm font-medium text-foreground',
							'hover:border-border focus:outline-none focus:border-primary/50',
							'transition-colors cursor-pointer'
						)}
					>
						{SORT_OPTIONS.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
					<ChevronDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
				</div>
				<button
					type="button"
					onClick={onSortOrderToggle}
					className={cn(
						'h-9 px-3 rounded-lg border border-border bg-background/70 backdrop-blur-sm',
						'text-sm font-medium text-foreground',
						'hover:border-foreground hover:bg-muted',
						'transition-colors'
					)}
				>
					{sortOrder === 'asc' ? '\u2191 Asc' : '\u2193 Desc'}
				</button>
			</div>
		</div>
	)
}

// ─── Pagination ───

function SimplePagination({
	page,
	totalPages,
	onPageChange,
}: {
	page: number
	totalPages: number
	onPageChange: (page: number) => void
}) {
	if (totalPages <= 1) return null

	return (
		<div className="flex items-center justify-center gap-2 py-4">
			<Button
				variant="outline"
				size="sm"
				onClick={() => onPageChange(page - 1)}
				disabled={page <= 1}
			>
				Previous
			</Button>
			<span className="text-sm text-muted-foreground">
				Page {page} of {totalPages}
			</span>
			<Button
				variant="outline"
				size="sm"
				onClick={() => onPageChange(page + 1)}
				disabled={page >= totalPages}
			>
				Next
			</Button>
		</div>
	)
}

// ─── Workspace Header ───

function WorkspaceHeader({
	workspaceName,
	workspaceLogo,
	title,
}: {
	workspaceName: string | null
	workspaceLogo: string | null
	title: string
}) {
	const initials = workspaceName
		? workspaceName
				.split(' ')
				.map((w) => w[0])
				.join('')
				.toUpperCase()
				.slice(0, 2)
		: null

	return (
		<header className="fixed top-0 inset-x-0 h-16 backdrop-blur-xl bg-background/80 border-b border-border/30 flex items-center px-8 sm:px-14 z-30">
			<div className="flex items-center gap-2.5 shrink-0">
				{workspaceLogo ? (
					<img
						src={workspaceLogo}
						alt={workspaceName || ''}
						className="w-7 h-7 rounded-md object-cover"
					/>
				) : initials ? (
					<div className="w-7 h-7 rounded-md bg-primary/10 text-primary text-xs font-medium flex items-center justify-center">
						{initials}
					</div>
				) : (
					<div className="w-7 h-7 rounded-md bg-neutral-900 flex items-center justify-center">
						<img src="/favicon.svg" alt="DAM" className="w-4 h-4" />
					</div>
				)}
				{workspaceName && (
					<span className="text-sm font-semibold text-foreground tracking-tight hidden sm:inline">
						{workspaceName}
					</span>
				)}
			</div>
			<div className="mx-3 h-5 w-px bg-border/50" />
			<h1 className="text-sm font-medium text-foreground truncate">{title}</h1>
		</header>
	)
}

// ─── Asset Detail View (matches /s/$token layout) ───

function AssetDetailView({
	token,
	assetId,
	previewToken,
	workspaceName,
	workspaceLogo,
	folderName,
	onBack,
}: {
	token: string
	assetId: string
	previewToken: string | null
	workspaceName: string | null
	workspaceLogo: string | null
	folderName: string
	onBack: () => void
}) {
	const {
		data: asset,
		isLoading,
		error,
	} = usePublicFolderAssetDetail(token, assetId, previewToken, { enabled: true })
	const [isDownloading, setIsDownloading] = useState(false)

	const handleDownload = async () => {
		if (!asset?.file) return
		setIsDownloading(true)
		try {
			const response = await fetch(`/api/public/folder-share/${token}/download/${asset.file.id}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ previewToken }),
			})
			if (!response.ok) throw new Error('Download failed')
			const blob = await response.blob()
			const url = URL.createObjectURL(blob)
			const a = document.createElement('a')
			a.href = url
			a.download = asset.file.filename
			document.body.appendChild(a)
			a.click()
			document.body.removeChild(a)
			URL.revokeObjectURL(url)
		} catch (err) {
			console.error('Download failed:', err)
		} finally {
			setIsDownloading(false)
		}
	}

	if (isLoading) {
		return (
			<div className="min-h-dvh flex flex-col px-8 sm:px-14 py-3">
				<div className="flex items-center gap-3 px-4 py-2">
					<Skeleton className="h-8 w-8" />
					<Skeleton className="h-5 w-48" />
				</div>
				<div className="flex-1 flex gap-3 overflow-hidden">
					<div className="flex-1 p-3">
						<Skeleton className="w-full h-full rounded-xl" />
					</div>
					<div className="w-[380px] hidden lg:block py-3">
						<Skeleton className="w-full h-full rounded-xl" />
					</div>
				</div>
			</div>
		)
	}

	if (error || !asset) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
							<AlertCircleIcon className="h-8 w-8 text-destructive" />
						</div>
						<CardTitle>Asset Not Found</CardTitle>
						<CardDescription>{error?.message || 'This asset could not be loaded'}</CardDescription>
					</CardHeader>
					<CardContent className="flex justify-center">
						<Button variant="outline" onClick={onBack}>
							<ArrowLeftIcon className="h-4 w-4 mr-2" />
							Back to folder
						</Button>
					</CardContent>
				</Card>
			</div>
		)
	}

	const file = asset.file
	const mediaType = file?.mediaTypeType?.toLowerCase()
	const isImage = mediaType === 'image'

	const previewApiUrl =
		asset.id && previewToken
			? `/api/public/folder-share/${token}/asset/${asset.id}/preview?pt=${previewToken}`
			: null

	return (
		<div className="min-h-dvh flex flex-col">
			{/* Header */}
			<WorkspaceHeader
				workspaceName={workspaceName}
				workspaceLogo={workspaceLogo}
				title={folderName}
			/>

			{/* Content — offset for fixed header */}
			<div className="flex-1 flex flex-col pt-16 px-8 sm:px-14 py-3 min-h-0">
				{/* Title bar — full width above preview + panel */}
				<div className="flex items-center gap-3 py-2 flex-shrink-0">
					<button
						type="button"
						onClick={onBack}
						className="w-8 h-8 rounded-lg bg-muted/50 hover:bg-muted flex items-center justify-center transition-colors shrink-0"
						aria-label="Back to folder"
					>
						<ArrowLeftIcon className="w-4 h-4" />
					</button>

					<h2 className="text-sm font-semibold truncate min-w-0">{file?.filename || 'Unknown'}</h2>

					{file && (
						<Badge variant="secondary" className="flex-shrink-0 text-xs">
							{formatFileSize(file.size)}
						</Badge>
					)}

					{/* Download button */}
					<div className="ml-auto flex-shrink-0">
						<Button
							size="sm"
							variant="outline"
							className="gap-1.5 h-9 rounded-lg"
							onClick={handleDownload}
							disabled={isDownloading || !file}
						>
							{isDownloading ? (
								<LoaderIcon className="h-4 w-4 animate-spin" />
							) : (
								<DownloadIcon className="h-4 w-4" />
							)}
							<span className="hidden sm:inline">
								{isDownloading ? 'Downloading...' : 'Download'}
							</span>
						</Button>
					</div>
				</div>

				{/* Preview + right panel row */}
				<div className="flex-1 flex gap-3 overflow-hidden min-h-0">
					{/* Preview area */}
					<div className="flex-1 flex flex-col min-w-0 min-h-0 overflow-hidden p-3">
						{isImage && asset.thumbnailUrl ? (
							<div className="flex-1 flex items-center justify-center bg-muted/30 rounded-xl overflow-hidden">
								<img
									src={asset.thumbnailUrl}
									alt={file?.filename || ''}
									className="max-w-full max-h-full object-contain"
								/>
							</div>
						) : file ? (
							<QueryBoundary>
								<AssetPreview
									file={{
										id: file.id,
										filename: file.filename,
										size: file.size,
										fileType: file.mediaTypeSubtype,
										mediaTypeType: file.mediaTypeType,
										mediaTypeSubtype: file.mediaTypeSubtype,
										width: file.width,
										height: file.height,
										duration: file.duration,
									}}
									thumbnailUrl={asset.thumbnailUrl}
									className="flex-1 min-h-0"
									disableContextMenu
									sharePreviewUrl={previewApiUrl}
									isPublic
								/>
							</QueryBoundary>
						) : (
							<div className="flex-1 flex items-center justify-center bg-muted/30 rounded-xl">
								<div className="text-center">
									<DownloadIcon className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
									<p className="text-sm text-muted-foreground">Preview not available</p>
								</div>
							</div>
						)}
					</div>

					{/* Right panel — file info */}
					<div className="flex-shrink-0 h-full hidden lg:block py-3">
						<div className="w-[380px] h-full rounded-xl border border-border/30 dark:border-white/[0.045] bg-background dark:bg-background/70 backdrop-blur-lg overflow-hidden flex flex-col">
							<div className="p-4 border-b border-border/30">
								<h3 className="text-sm font-semibold">File Info</h3>
							</div>
							<ScrollArea className="flex-1">
								<div className="p-4 space-y-4">
									{file && (
										<>
											<InfoRow label="Filename" value={file.filename} />
											<InfoRow label="Size" value={formatFileSize(file.size)} />
											{file.mediaTypeType && file.mediaTypeSubtype && (
												<InfoRow
													label="Type"
													value={`${file.mediaTypeType}/${file.mediaTypeSubtype}`}
												/>
											)}
											{file.width && file.height && (
												<InfoRow label="Dimensions" value={`${file.width} \u00D7 ${file.height}`} />
											)}
											{file.duration != null && (
												<InfoRow
													label="Duration"
													value={`${Math.floor(file.duration / 60)}:${String(file.duration % 60).padStart(2, '0')}`}
												/>
											)}
											{asset.dateStaged && (
												<InfoRow
													label="Date Staged"
													value={new Date(asset.dateStaged).toLocaleDateString('en-US', {
														year: 'numeric',
														month: 'short',
														day: 'numeric',
													})}
												/>
											)}
											{asset.dateLive && (
												<InfoRow
													label="Date Live"
													value={new Date(asset.dateLive).toLocaleDateString('en-US', {
														year: 'numeric',
														month: 'short',
														day: 'numeric',
													})}
												/>
											)}
										</>
									)}
								</div>
							</ScrollArea>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function InfoRow({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<dt className="text-xs text-muted-foreground mb-0.5">{label}</dt>
			<dd className="text-sm text-foreground break-all">{value}</dd>
		</div>
	)
}

// ─── Main Page Component ───

function PublicFolderPage() {
	const { token } = Route.useParams()
	const { folderId, assetId, page, sortBy, sortOrder } = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })

	const [password, setPassword] = useState('')
	const [passwordError, setPasswordError] = useState<string | null>(null)

	// Check for stored unlock
	const storedUnlock = getStoredUnlock(token)
	const [isUnlocked, setIsUnlocked] = useState(!!storedUnlock)
	const [previewToken, setPreviewToken] = useState<string | null>(
		storedUnlock?.previewToken ?? null
	)
	const [_downloadingFileId, setDownloadingFileId] = useState<string | null>(null)

	// Fetch share info
	const { data: shareInfo, isLoading: infoLoading, error: infoError } = useFolderShareInfo(token)

	// Determine effective preview token
	const effectivePt = previewToken || shareInfo?.previewToken || null

	const needsPassword = shareInfo?.hasPassword && !isUnlocked
	const hasAccess = !needsPassword

	// Fetch folder data (only when we have access and not viewing an asset)
	const { data: foldersData, isLoading: foldersLoading } = usePublicFolderChildren(
		token,
		folderId,
		effectivePt,
		{ enabled: hasAccess && !assetId }
	)
	const { data: assetsData, isLoading: assetsLoading } = usePublicFolderAssets(
		token,
		folderId,
		page,
		50,
		effectivePt,
		sortBy,
		sortOrder,
		{ enabled: hasAccess && !assetId }
	)

	// Verify password
	const verifyMutation = useVerifyFolderSharePassword(token)

	const handleUnlock = () => {
		setPasswordError(null)
		verifyMutation.mutate(password, {
			onSuccess: (data) => {
				setIsUnlocked(true)
				if (data.previewToken) {
					setPreviewToken(data.previewToken)
					saveUnlock(token, data.previewToken)
				}
			},
			onError: (err) => {
				setPasswordError(err.message)
			},
		})
	}

	// Navigation
	const handleFolderNavigate = useCallback(
		(newFolderId?: string) => {
			navigate({
				search: { folderId: newFolderId, assetId: undefined, page: 1, sortBy, sortOrder },
			})
		},
		[navigate, sortBy, sortOrder]
	)

	const handleAssetClick = useCallback(
		(assetClickId: string) => {
			navigate({
				search: { folderId, assetId: assetClickId, page, sortBy, sortOrder },
			})
		},
		[navigate, folderId, page, sortBy, sortOrder]
	)

	const handleBackFromAsset = useCallback(() => {
		navigate({
			search: { folderId, assetId: undefined, page, sortBy, sortOrder },
		})
	}, [navigate, folderId, page, sortBy, sortOrder])

	const handlePageChange = useCallback(
		(newPage: number) => {
			navigate({
				search: { folderId, assetId: undefined, page: newPage, sortBy, sortOrder },
			})
		},
		[navigate, folderId, sortBy, sortOrder]
	)

	const handleSortByChange = useCallback(
		(newSortBy: string) => {
			navigate({
				search: { folderId, assetId: undefined, page: 1, sortBy: newSortBy, sortOrder },
			})
		},
		[navigate, folderId, sortOrder]
	)

	const handleSortOrderToggle = useCallback(() => {
		navigate({
			search: {
				folderId,
				assetId: undefined,
				page: 1,
				sortBy,
				sortOrder: sortOrder === 'asc' ? 'desc' : 'asc',
			},
		})
	}, [navigate, folderId, sortBy, sortOrder])

	// Download
	const _handleDownload = useCallback(
		async (fileId: string, filename: string) => {
			setDownloadingFileId(fileId)
			try {
				const response = await fetch(`/api/public/folder-share/${token}/download/${fileId}`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ previewToken: effectivePt }),
				})
				if (!response.ok) throw new Error('Download failed')
				const blob = await response.blob()
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = filename
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)
			} catch (err) {
				console.error('Download failed:', err)
			} finally {
				setDownloadingFileId(null)
			}
		},
		[token, effectivePt]
	)

	// Error state
	if (infoError) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
							<AlertCircleIcon className="h-8 w-8 text-destructive" />
						</div>
						<CardTitle>Link Unavailable</CardTitle>
						<CardDescription>{infoError.message}</CardDescription>
					</CardHeader>
				</Card>
			</div>
		)
	}

	// Loading
	if (infoLoading) {
		return (
			<div className="min-h-screen bg-background">
				<div className="px-8 sm:px-14 py-6 pt-8">
					<Skeleton className="h-8 w-48 mb-6" />
					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
						{Array.from({ length: 6 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: Loading skeleton
							<div key={`skel-${i}`} className="animate-pulse">
								<div className="aspect-square rounded-lg bg-muted" />
								<div className="mt-3 h-4 w-3/4 rounded bg-muted" />
								<div className="mt-1.5 h-3 w-1/2 rounded bg-muted" />
							</div>
						))}
					</div>
				</div>
			</div>
		)
	}

	if (!shareInfo) return null

	// Password wall
	if (needsPassword) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background p-4">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
							<LockIcon className="h-8 w-8 text-primary" />
						</div>
						<CardTitle>Password Protected</CardTitle>
						<CardDescription>Enter the password to view "{shareInfo.folderName}"</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<Input
							type="password"
							placeholder="Enter password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
						/>
						{passwordError && (
							<p className="text-sm text-destructive text-center">{passwordError}</p>
						)}
						<Button
							onClick={handleUnlock}
							disabled={!password || verifyMutation.isPending}
							className="w-full gap-2"
						>
							{verifyMutation.isPending ? (
								<>
									<LoaderIcon className="h-4 w-4 animate-spin" />
									Verifying...
								</>
							) : (
								<>
									<ShieldCheckIcon className="h-4 w-4" />
									Unlock
								</>
							)}
						</Button>
					</CardContent>
				</Card>
			</div>
		)
	}

	// ─── Asset Detail View ───
	if (assetId) {
		return (
			<AssetDetailView
				token={token}
				assetId={assetId}
				previewToken={effectivePt}
				workspaceName={shareInfo.workspaceName}
				workspaceLogo={shareInfo.workspaceLogo}
				folderName={shareInfo.folderName}
				onBack={handleBackFromAsset}
			/>
		)
	}

	// ─── Folder Listing View ───

	const folders = foldersData?.folders || []
	const assets = assetsData?.assets || []
	const isAtRoot = !folderId
	const isLoading = foldersLoading || assetsLoading

	return (
		<div className="min-h-screen bg-background">
			<WorkspaceHeader
				workspaceName={shareInfo.workspaceName}
				workspaceLogo={shareInfo.workspaceLogo}
				title={shareInfo.folderName}
			/>

			{/* Content area */}
			<div className="pt-20 px-8 sm:px-14 pb-8">
				{/* Breadcrumbs */}
				{folderId && (
					<div className="mb-4">
						<PublicBreadcrumbs
							token={token}
							folderId={folderId}
							previewToken={effectivePt}
							onNavigate={handleFolderNavigate}
						/>
					</div>
				)}

				{isLoading && folders.length === 0 && assets.length === 0 ? (
					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
						{Array.from({ length: 6 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: Loading skeleton
							<div key={`skel-${i}`} className="animate-pulse">
								<div className="aspect-square rounded-lg bg-muted" />
								<div className="mt-3 h-4 w-3/4 rounded bg-muted" />
								<div className="mt-1.5 h-3 w-1/2 rounded bg-muted" />
							</div>
						))}
					</div>
				) : (
					<>
						{/* Folder cards */}
						{folders.length > 0 && (
							<div className="mb-8">
								{!isAtRoot && (
									<h3 className="text-sm font-medium text-foreground mb-3">Subfolders</h3>
								)}
								{isAtRoot ? (
									<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 sm:gap-8">
										{folders.map((f) => (
											<PublicFolderCard
												key={f.id}
												folder={f}
												onClick={() => handleFolderNavigate(f.id)}
												variant="large"
											/>
										))}
									</div>
								) : (
									<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-3">
										{folders.map((f) => (
											<PublicFolderCard
												key={f.id}
												folder={f}
												onClick={() => handleFolderNavigate(f.id)}
												variant="compact"
											/>
										))}
									</div>
								)}
							</div>
						)}

						{/* Assets */}
						{(assets.length > 0 || (assetsData && assetsData.total > 0)) && (
							<div>
								<PublicSortToolbar
									totalCount={assetsData?.total ?? 0}
									sortBy={sortBy}
									sortOrder={sortOrder}
									onSortByChange={handleSortByChange}
									onSortOrderToggle={handleSortOrderToggle}
								/>
								<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
									{assets.map((asset) => (
										<PublicAssetCard
											key={asset.id}
											asset={asset}
											onClick={() => handleAssetClick(asset.id)}
										/>
									))}
								</div>
								<SimplePagination
									page={assetsData?.page || 1}
									totalPages={assetsData?.totalPages || 1}
									onPageChange={handlePageChange}
								/>
							</div>
						)}

						{/* Empty state */}
						{folders.length === 0 && assets.length === 0 && !isLoading && (
							<div className="flex flex-col items-center justify-center py-20 text-center">
								<FolderOpen className="w-12 h-12 text-muted-foreground mb-4" />
								<p className="text-muted-foreground">This folder is empty</p>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	)
}
